import type { ReactNode } from 'react';
import { ArrowPathIcon, CubeIcon } from '@heroicons/react/24/outline';
import { NEUTRAL_BUTTON_CLASS } from '../../details/CredsActionButtons';
import { Spinner } from '../BrowserPrimitives';

interface ViewerMessageProps {
    title: string;
    detail?: string;
    busy?: boolean;
    percent?: number | null;
    actionLabel?: string;
    actionIcon?: typeof ArrowPathIcon;
    onAction?: () => void;
    children?: ReactNode;
}

function ProgressBar({ percent }: { percent: number }) {
    return (
        <div
            role="progressbar"
            aria-label="Download progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            className="flex w-[240px] items-center gap-2"
        >
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200">
                <div
                    className="h-full rounded-full bg-gray-800 transition-[width]"
                    style={{ width: `${percent}%` }}
                />
            </div>
            <span className="w-9 text-right text-[12px] tabular-nums text-gray-500">
                {percent}%
            </span>
        </div>
    );
}

export function ViewerMessage({
    title,
    detail,
    busy,
    percent,
    actionLabel,
    actionIcon: ActionIcon = ArrowPathIcon,
    onAction,
    children
}: ViewerMessageProps) {
    const hasAction = Boolean(actionLabel && onAction);
    return (
        <div
            className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center"
            aria-live="polite"
            aria-busy={busy}
        >
            {busy ? (
                <Spinner className="h-6 w-6" />
            ) : (
                <CubeIcon
                    className="h-10 w-10 text-gray-300"
                    strokeWidth={1.2}
                    aria-hidden="true"
                />
            )}
            <p className="text-sm font-medium text-gray-700 max-w-[420px]">{title}</p>
            {percent != null && <ProgressBar percent={percent} />}
            {detail && <p className="text-[13px] text-gray-500 max-w-[420px]">{detail}</p>}
            {(hasAction || children) && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                    {children}
                    {hasAction && (
                        <button type="button" onClick={onAction} className={NEUTRAL_BUTTON_CLASS}>
                            <ActionIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            {actionLabel}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
